// Service to fetch and manage LVC scenarios
// Fetches from backend API which manages scenarios in the filesystem
import { getIdToken } from '../contexts/AuthContext';
import { buildApiUrl, getAuthHeaders } from '../config/api';

/**
 * Fetches all LVC scenarios from the backend API
 */
export const fetchScenarios = async () => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl('/lvc/scenarios'), {
      headers: getAuthHeaders(idToken)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch scenarios: ${response.statusText}`);
    }
    const scenarios = await response.json();
    return scenarios;
  } catch (error) {
    console.error('Error fetching scenarios:', error);
    return [];
  }
};

/**
 * Creates a new LVC scenario
 * @param {string} name - Scenario name
 * @param {string|null} skiAreaId - Optional ski area ID (null for "Unlisted")
 */
export const createScenario = async (name, skiAreaId = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl('/lvc/scenarios'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({ name, ski_area_id: skiAreaId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to create scenario: ${response.statusText}`);
    }

    const scenario = await response.json();
    return scenario;
  } catch (error) {
    console.error('Error creating scenario:', error);
    throw error;
  }
};

/**
 * Fetches a specific scenario by name
 */
export const fetchScenario = async (scenarioName) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/lvc/scenarios/${encodeURIComponent(scenarioName)}`), {
      headers: getAuthHeaders(idToken)
    });
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Scenario not found');
      }
      throw new Error(`Failed to fetch scenario: ${response.statusText}`);
    }
    const scenario = await response.json();
    return scenario;
  } catch (error) {
    console.error(`Error fetching scenario ${scenarioName}:`, error);
    throw error;
  }
};

/**
 * Updates a scenario
 */
export const updateScenario = async (scenarioName, scenarioData) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/lvc/scenarios/${encodeURIComponent(scenarioName)}`), {
      method: 'PUT',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify(scenarioData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to update scenario: ${response.statusText}`);
    }

    const scenario = await response.json();
    return scenario;
  } catch (error) {
    console.error('Error updating scenario:', error);
    throw error;
  }
};

/**
 * Deletes a scenario
 * @param {string} scenarioName - The name/id of the scenario to delete
 */
export const deleteScenario = async (scenarioName) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/lvc/scenarios/${encodeURIComponent(scenarioName)}`), {
      method: 'DELETE',
      headers: getAuthHeaders(idToken),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to delete scenario: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error deleting scenario:', error);
    throw error;
  }
};
